import { DefaultTheme } from 'styled-components';

/**
 * createVariantSelector creates a helper function which can be used to
 * interpolate variant styles into styled-components.
 *
 * Example:
 *
 * ```ts
 * type SizeVariant = 'small' | 'large';
 *
 * interface VariantProps {
 *  size: SizeVariant;
 * }
 *
 * const variantSizeSelector = createVariantSelector<SizeVariant, VariantProps>('size', ['small', 'large']);
 *
 * const VariableStyledComponent = styled.div`
 *  font-size: ${variantSizeSelector('typography', ['textSm', 'textLg'])}
 * `
 * ```
 * @param key The key which identifies the variant property
 * @param variants An array defining in which order the variants should be mapped
 */
export default function createVariantSelector<K>(
  key: keyof K,
  variants: K[typeof key][],
) {
  return function selector<
    J extends keyof any &
      {
        [J in keyof DefaultTheme]: DefaultTheme[J] extends Record<
          string,
          unknown
        >
          ? J
          : never;
      }[keyof DefaultTheme]
  >(themeKey: J, values: (keyof DefaultTheme[J])[]) {
    return <P extends { theme: DefaultTheme } & { [k in keyof K]: K[k] }>(
      p: P,
    ) => {
      const idx = variants.findIndex(v => v === p[key]);
      const value = values[idx];
      return `${p.theme[themeKey][value]}`;
    };
  };
}
